import { supabase } from '../supabase/config';
import { logActivity } from '../utils/activityLogger';
import { slugify } from '../utils/slugify';

// Canvas ile görseli hedef boyut ve orana göre yeniden boyutlandırır
const resizeImage = (file, maxW, maxH, fit = 'cover') => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = maxW;
            canvas.height = maxH;
            const ctx = canvas.getContext('2d');

            // cover: oranı koruyarak kırp, contain: sığdır
            const srcRatio = img.width / img.height;
            const dstRatio = maxW / maxH;
            let sx = 0, sy = 0, sw = img.width, sh = img.height;

            if (fit === 'cover') {
                if (srcRatio > dstRatio) {
                    sw = img.height * dstRatio;
                    sx = (img.width - sw) / 2;
                } else {
                    sh = img.width / dstRatio;
                    sy = (img.height - sh) / 2;
                }
            }

            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, maxW, maxH);
            canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Resize failed')), 'image/jpeg', 0.85);
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
};

export const settingsService = {
    updateTenantField: async (effectiveTenantId, fieldPath, value, userData, user, logMessage) => {
        try {
            const fieldMapping = {
                loyaltyLevels: 'loyalty_levels',
                loyaltyRules: 'loyalty_rules',
                visitRoles: 'visit_roles',
                apiKeys: 'api_keys',
                employeeCount: 'employee_count',
                workingHours: 'working_hours',
                bannerUrl: 'banner_url',
                socialMedia: 'social_media',
                googleMapsUrl: 'google_maps_url'
            };

            const mapField = (field) => fieldMapping[field] || field;

            let updatePayload;

            if (fieldPath === 'profile') {
                // Determine unique slug from the new name
                let baseSlug = slugify(value.name || 'yeni-isletme');
                let finalSlug = baseSlug;
                let counter = 2;
                while (true) {
                    const { data: existing } = await supabase
                        .from('tenants')
                        .select('id')
                        .eq('slug', finalSlug)
                        .neq('id', effectiveTenantId)
                        .limit(1);
                        
                    if (!existing || existing.length === 0) break;
                    finalSlug = `${baseSlug}-${counter}`;
                    counter++;
                }

                // Special case for batch profile update
                updatePayload = {
                    name: value.name,
                    slug: finalSlug,
                    industry: value.industry,
                    phone: value.phone,
                    social_media: value.socialMedia,
                    working_hours: value.workingHours,
                    city: value.city,
                    district: value.district,
                    address: value.address,
                    google_maps_url: value.googleMapsUrl
                };
            } else if (fieldPath.includes('.')) {
                // Handle nested field paths like 'theme.logoUrl'
                const parts = fieldPath.split('.');
                const topLevelKey = mapField(parts[0]);

                // Fetch current tenant data to merge
                const { data: tenant, error: fetchError } = await supabase
                    .from('tenants')
                    .select(topLevelKey)
                    .eq('id', effectiveTenantId)
                    .single();
                if (fetchError) throw fetchError;

                // Build nested update by merging
                let current = tenant?.[topLevelKey] || {};
                let ref = current;
                for (let i = 1; i < parts.length - 1; i++) {
                    const p = parts[i]; // nested keys are usually kept as is in JSONB
                    if (!ref[p] || typeof ref[p] !== 'object') {
                        ref[p] = {};
                    }
                    ref = ref[p];
                }
                ref[parts[parts.length - 1]] = value;

                updatePayload = { [topLevelKey]: current };
            } else {
                updatePayload = { [mapField(fieldPath)]: value };
            }

            const { error } = await supabase
                .from('tenants')
                .update(updatePayload)
                .eq('id', effectiveTenantId);
            if (error) throw error;

            if (logMessage) {
                await logActivity(effectiveTenantId, userData?.email || user?.email, user?.uid, 'UPDATE_SETTINGS', logMessage);
            }
        } catch (error) {
            console.error(`Error updating ${fieldPath}:`, error);
            throw error;
        }
    },

    uploadLogo: async (effectiveTenantId, file, userData, user) => {
        try {
            const resized = await resizeImage(file, 256, 256, 'cover');
            const filePath = `${effectiveTenantId}/logo_${Date.now()}`;

            const { error: uploadError } = await supabase.storage
                .from('logos')
                .upload(filePath, resized, { upsert: true, contentType: 'image/jpeg' });
            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('logos')
                .getPublicUrl(filePath);
            const url = data.publicUrl;

            // Fetch current theme to merge logoUrl
            const { data: tenant, error: fetchError } = await supabase
                .from('tenants')
                .select('theme')
                .eq('id', effectiveTenantId)
                .single();
            if (fetchError) throw fetchError;

            const currentTheme = tenant?.theme || {};
            const { error: updateError } = await supabase
                .from('tenants')
                .update({ theme: { ...currentTheme, logoUrl: url } })
                .eq('id', effectiveTenantId);
            if (updateError) throw updateError;

            await logActivity(effectiveTenantId, userData?.email || user?.email, user?.uid, 'UPDATE_THEME', 'Updated company logo');
            return url;
        } catch (error) {
            console.error("Error uploading logo:", error);
            throw error;
        }
    },

    uploadBanner: async (effectiveTenantId, file, userData, user) => {
        try {
            const resized = await resizeImage(file, 960, 540, 'cover');
            const filePath = `${effectiveTenantId}/banner_${Date.now()}`;

            const { error: uploadError } = await supabase.storage
                .from('logos')
                .upload(filePath, resized, { upsert: true, contentType: 'image/jpeg' });
            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('logos')
                .getPublicUrl(filePath);
            const url = data.publicUrl;

            const { error: updateError } = await supabase
                .from('tenants')
                .update({ banner_url: url })
                .eq('id', effectiveTenantId);
            if (updateError) throw updateError;

            await logActivity(effectiveTenantId, userData?.email || user?.email, user?.uid, 'UPDATE_BANNER', 'Updated company banner');
            return url;
        } catch (error) {
            console.error("Error uploading banner:", error);
            throw error;
        }
    }
};
